import { Box, Button, Stack, Typography } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { SystemStyleObject } from "@mui/system";
import { DoNotDisturbOutlined } from "@mui/icons-material";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import strings from "global/constants/StringConstants";
import { ModalLockIcon } from "global/components/AppModal";
import FEATURE_UPGRADE_COPY from "global/constants/FeatureGateCopy";
import featureGateTokens from "global/components/FeatureGateBlock/FeatureGate.tokens";
import { FeatureState } from "hooks/use-feature";

interface FeatureBannerProps {
  /** Straight from `useFeatureState(key)` — renders nothing when "available". */
  state: FeatureState;
  /** Looks up plan-specific copy in `FeatureGateCopy.ts`, same as `UpgradeModal`. */
  featureKey?: string;
  /** Display name of the gated feature, e.g. "Bulk send". Used in the default
   *  copy when `featureKey` has no specific entry; ignored if `title` is set. */
  featureName?: string;
  title?: string;
  message?: string;
  /** `page` — full-width card under a section title (default).
   *  `compact` — one line, for inside a card or above a table. */
  variant?: "page" | "compact";
  /** Single style object only (not the array `sx` form) — kept simple since
   *  every call site passes plain overrides like spacing or margin. */
  sx?: SystemStyleObject<Theme>;
}

const t = featureGateTokens;

/**
 * The page/inline half of the "why is this blocked" system — `AppModal`'s
 * `UpgradeModal`/`MaintenanceBanner` cover the click-triggered popup case,
 * `FeatureGateBlock` covers "the whole screen/panel is blocked"; this covers
 * everywhere a control is disabled in place and the page should still explain
 * why, without a popup.
 */
const FeatureBanner = (props: FeatureBannerProps) => {
  if (props.state.status === "available") return null;

  const isUpgrade = props.state.status === "not-in-plan";
  const tone = t.tone[isUpgrade ? "upgrade" : "off"];
  const isCompact = props.variant === "compact";

  const copy = props.featureKey
    ? FEATURE_UPGRADE_COPY[props.featureKey]
    : undefined;

  /*
    The feature's display name, wherever it comes from. `FeatureGateCopy` stores
    it bare ("Custom branding") because `UpgradeModal` uses it as a heading; the
    banner is a sentence, so it reads the same either way and every gated screen
    opens with "<feature> isn't included in your plan".
  */
  const featureLabel = copy?.title ?? props.featureName;
  const title =
    props.title ??
    (isUpgrade
      ? featureLabel
        ? `${featureLabel} isn't included in your plan`
        : strings.FEATURE_GATE_UPGRADE_TITLE
      : featureLabel
        ? `${featureLabel} is switched off`
        : strings.FEATURE_GATE_MAINTENANCE_TITLE);
  const message =
    props.message ??
    (isUpgrade
      ? (copy?.message ?? strings.FEATURE_GATE_UPGRADE_DESCRIPTION_FULL)
      : (props.state.status === "admin-disabled" &&
          props.state.disabledMessage) ||
        strings.FEATURE_GATE_FALLBACK_MAINTENANCE_MESSAGE);

  const handleUpgrade = () => {
    history.push(viewpaths.viewMyPlanViewPath);
  };

  const icon = isUpgrade ? (
    <ModalLockIcon size={isCompact ? 15 : 19} />
  ) : (
    <DoNotDisturbOutlined sx={{ fontSize: isCompact ? 15 : 19 }} />
  );

  if (isCompact) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={[
          {
            bgcolor: tone.bg,
            border: `1px solid ${tone.border}`,
            borderRadius: "11px",
            px: 1.75,
            py: 1.1,
          },
          props.sx ?? {},
        ]}
      >
        <Box sx={{ color: tone.icon, display: "flex", flexShrink: 0 }}>
          {icon}
        </Box>
        <Typography
          sx={{ flex: 1, minWidth: 0, fontSize: "12.5px", color: t.color.body }}
        >
          <Box component="span" sx={{ fontWeight: 800, color: t.color.ink }}>
            {title}.
          </Box>{" "}
          {message}
        </Typography>
        {isUpgrade && (
          <Box
            component="button"
            type="button"
            onClick={handleUpgrade}
            sx={{
              border: "none",
              background: "none",
              p: 0,
              cursor: "pointer",
              flexShrink: 0,
              fontFamily: "inherit",
              fontSize: "12.5px",
              fontWeight: 700,
              color: tone.text,
              "&:hover": { color: tone.textHover },
            }}
          >
            {strings.FEATURE_GATE_VIEW_PLANS_BUTTON}
          </Box>
        )}
      </Stack>
    );
  }

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      alignItems={{ xs: "stretch", sm: "flex-start" }}
      spacing={1.75}
      sx={[
        {
          bgcolor: tone.bg,
          border: `1px solid ${tone.border}`,
          borderRadius: "13px",
          px: { xs: 2, sm: 2.25 },
          py: 2,
        },
        props.sx ?? {},
      ]}
    >
      <Stack direction="row" spacing={1.75} sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            width: { xs: 34, sm: 40 },
            height: { xs: 34, sm: 40 },
            flexShrink: 0,
            borderRadius: "11px",
            bgcolor: tone.tile,
            color: tone.icon,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>
        <Stack spacing={0.375} sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: { xs: 13, sm: "13.5px" },
              fontWeight: 800,
              color: t.color.ink,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 12, sm: "12.5px" },
              lineHeight: 1.55,
              color: t.color.muted,
            }}
          >
            {message}
          </Typography>
        </Stack>
      </Stack>
      {isUpgrade && (
        <Button
          variant="contained"
          onClick={handleUpgrade}
          sx={{
            height: 40,
            px: 2.25,
            fontWeight: 700,
            flexShrink: 0,
            width: { xs: "100%", sm: "auto" },
          }}
        >
          {strings.FEATURE_GATE_VIEW_PLANS_BUTTON}
        </Button>
      )}
    </Stack>
  );
};

export default FeatureBanner;
